import React, {useState} from 'react'
import { PickupNewOnCall } from '../../../models/PickupNewOnCall';
import { PickupStop } from '../../../models/ReroutePickupsRequest';

import OptionPicker from '../../Common/OptionPicker';
import ConfirmationPopup from '../../Common/ConfirmationPopup';
import LabeledProperty from './LabeledProperty';
import TerritoryBreak from './TerritoryBreak';

function NewOncall({
    pickups,
    routes,
    selectedTerritory,
    routePickup,
  }: {
    pickups: PickupNewOnCall[];
    routes: { [k: string]: string[] };
    selectedTerritory?: string | number;
    routePickup: (toRoute: string, stops: PickupStop[]) => void;
  }) {
    const [routeSelectionOpen, setRouteSelectionOpen] = useState(false);
    const [selectedPickup, setSelectedPickup] = useState<PickupNewOnCall | undefined>();
    const [selectedRoute, setSelectedRoute] = useState<string | undefined>();
    const [validRoutes, setValidRoutes] = useState<string[]>([]);
  
    const [confirmationIsOpen, setConfirmationIsOpen] = useState(false);
  
    const pickupsGroupedByTPC = pickups.reduce<{ [k: string]: PickupNewOnCall[] }>((acc, el) => {
      const territory = el.territoryName ? el.territoryName : 'NA';
  
      if (!acc[territory]) acc[territory] = [];
      acc[territory].push(el);
      return acc;
    }, {});
  
    const openRouteSelect = (pickup: PickupNewOnCall) => {
      setSelectedPickup(pickup);
      setValidRoutes(routes[pickup.territoryName]);
  
      setRouteSelectionOpen(true);
    };
  
    const selectRoute = (route: string) => {
      setSelectedRoute(route);
      setConfirmationIsOpen(true);
    };
  
    const confirmRoute = async () => {
      if (selectedRoute && selectedPickup) routePickup(selectedRoute, [{ locationId: selectedPickup.stopId, type: 'O' }]);
      setSelectedPickup(undefined);
      setSelectedRoute(undefined);
    };
  
    // Do not show this component if all pickups have been filtered out
    if (selectedTerritory && selectedTerritory !== 'All' && !pickupsGroupedByTPC[selectedTerritory]) return <></>;
  
    return (
      <div className="my-5">
        <OptionPicker
          title="Select route to move to"
          options={validRoutes}
          isOpen={routeSelectionOpen}
          setIsOpen={setRouteSelectionOpen}
          selectOption={selectRoute}
        />
        <ConfirmationPopup
          title="please confirm"
          message={`Dispatch this pickup to route ${selectedRoute}?`}
          isOpen={confirmationIsOpen}
          setIsOpen={setConfirmationIsOpen}
          onConfirm={confirmRoute}
        />
        <div className="flex grow flex-row bg-slate-600">
          <div className="flex grow">
            <div className="grid grow grid-cols-12 space-x-1 py-1 font-mono uppercase text-white shadow sm:text-lg md:text-2xl">
              <div className="col-span-4 pl-2 md:col-span-3">Call Time</div>
              <div className="col-span-9 hidden flex-row md:flex">
                <div className="w-3/5">Company Name/Address</div>
                <div className="w-2/5">City/Zip</div>
              </div>
              <div className="col-span-8 flex justify-center md:hidden md:justify-start">Address</div>
  
              <div className="justify-end"></div>
            </div>
          </div>
          <div className="w-20"></div>
        </div>
        {Object.entries(pickupsGroupedByTPC).map(([territoryName, territoryPickups]) => {
          if (selectedTerritory && selectedTerritory !== 'All' && selectedTerritory !== territoryName)
            return <div key={territoryName}></div>;
  
          return (
            <div key={territoryName}>
              {selectedTerritory === 'All' && <TerritoryBreak territory={territoryName} />}
              <div>
                {territoryPickups.map((pickup) => {
                  return <NewOncallRow key={pickup.stopId} pickup={pickup} openRouteSelect={openRouteSelect} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  
  function NewOncallRow({
    pickup,
    openRouteSelect,
  }: {
    pickup: PickupNewOnCall;
    openRouteSelect: (p: PickupNewOnCall) => void;
  }) {
    const [isExpanded, setIsExpanded] = useState(false);
  
    return (
      <div className="flex flex-col border-b border-gray-400 hover:cursor-pointer">
        <div className="flex flex-row">
          <div className="grid grow grid-cols-12 py-2" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="col-span-4 flex items-center justify-center text-xl md:col-span-3">
              {new Date(pickup.callTime).toLocaleTimeString()}
            </div>
            <div className="col-span-9 hidden flex-row md:flex">
              <div className="w-3/5">
                <div>{pickup.companyName}</div>
                <div>{pickup.address1}</div>
                {isExpanded && <div>{pickup.address2}</div>}
              </div>
              <div className="w-2/5">
                <div>{pickup.city}</div>
                <div>{pickup.zip}</div>
              </div>
            </div>
            <div className="col-span-8 flex flex-col justify-center md:hidden md:justify-start">
              <div>{pickup.companyName}</div>
              <div>{pickup.address1}</div>
              {isExpanded && <div>{pickup.address2}</div>}
  
              <div>
                {pickup.city} {pickup.state}, {pickup.zip}
              </div>
            </div>
          </div>
          <div className="flex w-20 items-center">
            <button className="btn-subtle" onClick={() => openRouteSelect(pickup)}>
              route
            </button>
          </div>
        </div>
        {isExpanded && (
          <div className="flex flex-col space-y-5 px-10 py-5">
            <div>
              <LabeledProperty title="Location Id" value={pickup.stopId} />
              <LabeledProperty title="Special Instructions" value={pickup.specialInstructions} />
            </div>
            <div>
              <LabeledProperty title="Ready Time" value={pickup.readyTime} timeString={true} />
              <LabeledProperty title="Scheduled Time" value={pickup.scheduleTime} timeString={true} />
              <LabeledProperty title="Close Time" value={pickup.closeTime} timeString={true} />
              <LabeledProperty title="Latest Time" value={pickup.latestTime} timeString={true} />
            </div>
          </div>
        )}
      </div>
    );
  }

export default NewOncall